import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  TextField,
  Tooltip,
  Box,
  Link,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import {
  Page,
  Header,
  Content,
  ContentHeader,
  Progress,
  ResponseErrorPanel,
  EmptyState,
} from '@backstage/core-components';
import { auth0OnboardingApiRef, AppSummary } from '../api';

// Template the Modify wizard opens. Keep in sync with the registered Day-2
// template name in app-config.yaml (catalog.locations).
const DAY2_TEMPLATE_PATH = '/create/templates/default/starter-app-day2';

/** Applications shown per page. */
const PAGE_SIZE = 6;

const RESOURCE_TOGGLES: { key: string; label: string }[] = [
  { key: 'enable_static_web', label: 'Static Web' },
  { key: 'enable_app_service', label: 'App Service' },
  { key: 'enable_container_app_jobs', label: 'Jobs' },
  { key: 'enable_postgres', label: 'Postgres' },
  { key: 'enable_object_storage', label: 'Storage' },
  { key: 'enable_key_vault', label: 'Key Vault' },
];

const useStyles = makeStyles(theme => ({
  // `grid-auto-rows: 1fr` makes every row as tall as the tallest one, so all
  // cards on the page share one height (MUI's Grid only equalises within a
  // row). The footer (date, owner, Modify) is pinned to the bottom so cards
  // line up regardless of how many chips or warnings each one carries.
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridAutoRows: '1fr',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    // `lg`, not `md`: the breakpoint is on the viewport, and the sidebar takes
    // ~220px of it — three columns at `md` leave cards too narrow to read.
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  content: { flexGrow: 1 },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
    '& > :last-child': { flexShrink: 0 },
  },
  footerMeta: {
    minWidth: 0,
    '& > *': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  chip: { marginRight: theme.spacing(0.5), marginBottom: theme.spacing(0.5) },
  meta: { color: theme.palette.text.secondary, fontSize: '0.8rem' },
  search: { marginBottom: theme.spacing(2), maxWidth: 400 },
  // Same yellow as the Day-2 warning boxes — this is a "heads up", not an error.
  pending: {
    border: '1px solid #ffb74d',
    borderRadius: 4,
    background: '#fff4e5',
    color: '#663c00',
  },
}));

function parseForm(app: AppSummary): Record<string, any> | null {
  if (!app.form_json) return null;
  try {
    return JSON.parse(app.form_json);
  } catch {
    return null;
  }
}

const INFRA_PR_URL =
  'https://dev.azure.com/arkance-devops/Be.Smart%20Infra/_git/application-iac-infra-live/pullrequest';

/** Open infra PR ids for a Day-2 edit that has been requested but not merged. */
function parsePendingPrIds(app: AppSummary): number[] {
  if (!app.pending_pr_ids) return [];
  try {
    const parsed = JSON.parse(app.pending_pr_ids);
    return Array.isArray(parsed) ? parsed.filter(id => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}

/** Exported for tests: this card is where an app's Day-2 availability is decided. */
export const AppCard = ({ app }: { app: AppSummary }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const form = useMemo(() => parseForm(app), [app]);
  const pendingPrIds = useMemo(() => parsePendingPrIds(app), [app]);
  const hasPending = pendingPrIds.length > 0;

  // Applications provisioned by the retired `app-infra` template. Day-2 manages
  // their INFRASTRUCTURE only: their repositories, pipelines and Azure DevOps
  // CI/CD were created by hand and stay that way, so the card has to say so —
  // otherwise the wizard looks like it manages the whole application.
  const infraOnly = form?.management_scope === 'infra_only';
  const migrationStatus: string = form?.migration_status ?? '';
  const migrationBlocked = ['pending', 'failed'].includes(migrationStatus);

  // Why Modify may be unavailable. Editing on top of an unmerged change would
  // open a second PR built from the OLD state, quietly reverting the first — so
  // the pending edit has to land (or be abandoned) before another one starts.
  // Why the MIGRATION blocks it, independent of anything else that might.
  // These two states are also the normal state right after a legacy app’s
  // first Day-2 run — the run opens a pull request AND sets the migration
  // pending — so the two reasons coexist routinely. Reusing `blockedReason`
  // below put the pull-request text under the "Terraform migration in
  // progress" heading, which told the reader to go merge a PR to fix a
  // migration.
  const MIGRATION_REASONS: Record<string, string> = {
    pending:
      'This app is being migrated to the current Terraform baseline. Wait for the infrastructure Apply to finish and for a platform operator to confirm the migration.',
    failed:
      'This app’s migration failed partway: some environments were migrated and others were not. A platform operator has to reconcile it from the live state before it can be modified.',
  };
  const migrationReason = MIGRATION_REASONS[migrationStatus] ?? '';

  let blockedReason = '';
  if (hasPending) {
    blockedReason =
      'This app already has an unmerged modification. Merge or abandon that Pull Request before making another change.';
  } else if (!form) {
    blockedReason =
      'No saved form — this app was created before Day-2 Modification and cannot be reloaded.';
  } else if (migrationReason) {
    // The Terraform baseline migration is merged but not confirmed applied.
    // Until it is, the live infrastructure and this snapshot disagree, and a
    // modification rendered from the snapshot would be rendered from a state
    // that does not exist yet.
    blockedReason = migrationReason;
  }

  const openModify = () => {
    if (!form) return;
    // `_original` carries the untouched snapshot so the Day-2 widgets can lock
    // fields and show destructive warnings by comparing against it.
    const formData = { ...form, _original: form };
    navigate(
      `${DAY2_TEMPLATE_PATH}?formData=${encodeURIComponent(
        JSON.stringify(formData),
      )}`,
    );
  };

  const environments: string[] = Array.isArray(form?.environments)
    ? form!.environments
    : [];
  const enabled = RESOURCE_TOGGLES.filter(t => form?.[t.key]);
  // Only an application whose source Day-2 created can be described by these
  // two fields. For an infrastructure-only app the frameworks are the
  // application team's business and nothing here ever learns them:
  // `reconstructForm` fills the locked dropdowns with placeholders so the form
  // stays valid, which is how `licensing` came to advertise an Express backend
  // it does not have (it is .NET) and `resourcemanagement` one it does not have
  // either (it is PHP/Symfony). A placeholder is not a fact, so it is not shown.
  const stack = infraOnly
    ? ''
    : [app.frontend, app.backend]
        .filter(v => v && v !== 'unknown')
        .join(' + ');
  const date = app.created_at
    ? new Date(app.created_at).toLocaleDateString()
    : '';
  // `requested_by` is an entity ref; the `user:default/` prefix is noise on a
  // card, so show the name and keep the full ref in the tooltip.
  const owner = app.requested_by?.replace(/^user:[^/]+\//, '') ?? '';

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <Typography variant="h6" className={classes.title} title={app.app_name}>
          {app.app_name}
        </Typography>
        {form?.resource_name && (
          <Typography className={classes.meta}>
            {form.resource_name}
            {stack ? ` · ${stack}` : ''}
          </Typography>
        )}
        <Box mt={1}>
          {enabled.map(t => (
            <Chip
              key={t.key}
              label={t.label}
              size="small"
              className={classes.chip}
            />
          ))}
        </Box>
        {environments.length > 0 && (
          <Box mt={0.5}>
            {environments.map(env => (
              <Chip
                key={env}
                label={env}
                size="small"
                variant="outlined"
                className={classes.chip}
              />
            ))}
          </Box>
        )}
        {/* This one IS a warning: the app cannot be modified right now. */}
        {migrationBlocked && (
          <Box mt={1} p={1} className={classes.pending}>
            <Typography variant="body2" component="div">
              <strong>
                {migrationStatus === 'pending'
                  ? 'Terraform migration in progress'
                  : 'Terraform migration failed'}
              </strong>
              <div>{migrationReason}</div>
            </Typography>
          </Box>
        )}
        {hasPending && (
          <Box mt={1} p={1} className={classes.pending}>
            <Typography variant="body2" component="div">
              <strong>⏳ Modification awaiting merge</strong>
              <div>
                The resources shown above are what is <strong>deployed today</strong>.
                A requested change is still open in{' '}
                {pendingPrIds.map((id, i) => (
                  <span key={id}>
                    {i > 0 && ', '}
                    <Link
                      href={`${INFRA_PR_URL}/${id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      PR !{id}
                    </Link>
                  </span>
                ))}
                . It takes effect only once merged and the pipeline runs.
              </div>
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions className={classes.footer}>
        <Box className={`${classes.meta} ${classes.footerMeta}`}>
          <div>{date}</div>
          {owner && <div title={app.requested_by ?? ''}>{owner}</div>}
        </Box>
        {blockedReason ? (
          <Tooltip title={blockedReason}>
            <span>
              <Button disabled startIcon={<EditIcon />}>
                Modify
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            startIcon={<EditIcon />}
            onClick={openModify}
          >
            Modify
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export const ModifyApplicationPage = () => {
  const classes = useStyles();
  const api = useApi(auth0OnboardingApiRef);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  // Apps appear here automatically: the wizard records a snapshot for every app
  // it creates, and the backend runs a scheduled discovery pass over
  // application-iac-infra-live for anything provisioned outside it. Nothing to
  // import by hand.
  const { value: apps, loading, error } = useAsync(() => api.listApps(), [api]);

  const filtered = useMemo(
    () =>
      (apps ?? [])
        // Only apps that actually exist in application-iac-infra-live. A row is
        // written the moment a Day-1 run finishes — before its infra PR is
        // merged, and even if it never is — so the table also holds apps with no
        // infrastructure at all. Offering to "modify" those is meaningless.
        .filter(a => !!a.infra_seen_at)
        .filter(a => a.app_name.toLowerCase().includes(query.toLowerCase())),
    [apps, query],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // A shrinking result set (e.g. while typing a search) can leave us on a page
  // that no longer exists — snap back into range.
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Page themeId="tool">
      <Header
        title="Modify Application"
        subtitle="Reopen a previously created app to change its resources, environments, or domains"
      />
      <Content>
        <ContentHeader title="Your applications" />
        {loading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}
        {!loading && !error && (
          <>
            <TextField
              className={classes.search}
              fullWidth
              variant="outlined"
              size="small"
              label="Search by name"
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            {filtered.length === 0 ? (
              <EmptyState
                missing="data"
                title="No applications found"
                description="Create a starter app first — it will show up here automatically."
              />
            ) : (
              <>
                <div className={classes.grid}>
                  {visible.map(app => (
                    <AppCard key={app.app_name} app={app} />
                  ))}
                </div>
                {pageCount > 1 && (
                  <Box mt={3} display="flex" justifyContent="center">
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(_e, value) => setPage(value)}
                      color="primary"
                      shape="rounded"
                    />
                  </Box>
                )}
                <Box mt={1} display="flex" justifyContent="center">
                  <Typography className={classes.meta}>
                    {`Showing ${visible.length} of ${filtered.length} application${
                      filtered.length === 1 ? '' : 's'
                    }`}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
